import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import db from '../lib/instant';
import CreateProjectModal from '../components/CreateProjectModal';
import InviteMemberModal from '../components/InviteMemberModal';

const Projects = () => {
  const user = db.useUser();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Query all data
  const { data, error } = db.useQuery(user ? {
    projects: {},
    project_members: {},
    users: {}
  } : {});

  // Filter projects where user is a member
  const userProjects = data?.project_members ? 
    data.project_members.filter(pm => pm.user_id === user?.id) : [];

  const handleInviteClick = (project) => {
    setSelectedProject(project);
    setShowInviteModal(true);
  };

  const handleProjectCreated = () => {
    // The query will automatically refresh due to InstantDB's reactivity
  };

  const getUserRole = (projectMember) => {
    return projectMember.role;
  };

  const canInviteMembers = (projectMember) => {
    const role = getUserRole(projectMember);
    return role === 'owner' || role === 'admin';
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Error loading projects. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600 mt-1">Organize your tasks into focused workspaces</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="button-primary bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 hover-lift"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
          </svg>
          <span>New Project</span>
        </button>
      </div>

      {userProjects.length === 0 ? (
        <div className="text-center py-16">
          <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
            <div className="text-gray-400 mb-6">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Create your first project</h3>
            <p className="text-gray-600 mb-6">Projects help you organize tasks around specific goals, events, or themes. Start collaborating with your team!</p>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="button-primary bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl transition-all hover-lift"
            >
              Get Started
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {userProjects.map((projectMember) => {
            // Find the project data from the projects array
            const project = data?.projects?.find(p => p.id === projectMember.project_id);
            const role = getUserRole(projectMember);
            const canInvite = canInviteMembers(projectMember);
            
            if (!project) return null;
            
            return (
              <div 
                key={projectMember.id} 
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover-lift cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900 truncate">{project.name}</h3>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          role === 'owner' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                          role === 'admin' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                          'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                          {role}
                        </span>
                      </div>
                      
                      {project.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">{project.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
                      </svg>
                      <span>{data?.project_members?.filter(pm => pm.project_id === project.id).length || 0} members</span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button 
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}`);
                        }}
                      >
                        Open →
                      </button>
                      {canInvite && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInviteClick(project);
                          }}
                          className="text-green-600 hover:text-green-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-green-50 transition-colors"
                        >
                          Invite
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Project card footer with gradient */}
                <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600"></div>
              </div>
            );
          })}
        </div>
      )}

      <CreateProjectModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProjectCreated={handleProjectCreated}
      />

      <InviteMemberModal 
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        projectId={selectedProject?.id}
        projectName={selectedProject?.name}
      />
    </div>
  );
};

export default Projects;